import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import {
  DeleteItemAttentionIcon,
  DeleteItemIcon,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseIcons/CurrentKnowledgeBaseIcons';
import { Button } from 'src/components/ui/button';
import {
  useDeleteKnowledgeBaseCollectorMutation,
  useDeleteKnowledgeBaseFilesMutation,
  useLazyGetKnowledgeBaseFilesQuery,
} from 'src/services/knowledgeBaseService';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import type { TreeNode } from 'src/utils/fileUtils';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const DeleteKnowledgeBaseIntegrationModal = ({
  knowledgeBaseId = null,
  disabled = false,
  item,
  trigger = null,
}: {
  knowledgeBaseId?: number | string | null;
  disabled?: boolean;
  // A knowledge base row, or a collector wrapped as one during onboarding.
  item: Pick<TreeNode, 'name' | 'type' | 'data'>;
  trigger?: React.ReactNode;
}) => {
  const notification = useNotification();
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const [deleteKnowledgeBaseFiles] = useDeleteKnowledgeBaseFilesMutation();
  const [deleteKnowledgeBaseIntegration, { isLoading: isLoadingDeleteKnowledgeBaseIntegration }] =
    useDeleteKnowledgeBaseCollectorMutation();
  const [getKnowledgeBaseFiles, { isLoading: isLoadingFiles }] =
    useLazyGetKnowledgeBaseFilesQuery();

  const close = () => {
    setOpen(false);
  };

  const onClick = async () => {
    const { data } = await getKnowledgeBaseFiles({
      id: knowledgeBaseId || id,
      sync: false,
    });

    const childs = data.filter((file) => file.collector_id === item.data.id);

    if (childs?.length) {
      await deleteKnowledgeBaseFiles({
        id: knowledgeBaseId || id,
        body: childs.map((i) => i.id) || [],
      });
    }

    const deleteKnowledgeBaseIntegrationResult = await deleteKnowledgeBaseIntegration({
      knowledgeBaseId: knowledgeBaseId || id,
      collectorId: item.data.id,
    });
    if (!('error' in deleteKnowledgeBaseIntegrationResult)) {
      notification.success(
        `${item.type.charAt(0).toUpperCase() + item.type.slice(1)} ${item.name} Deleted.`,
      );
      close();
    }
  };

  useLoaderWhile(isLoadingDeleteKnowledgeBaseIntegration, isLoadingFiles);

  return (
    <Dialog onOpenChange={setOpen} open={disabled ? false : open}>
      <DialogTrigger disabled={disabled} asChild>
        {trigger || (
          <button
            className={clsx(
              disabled && 'pointer-events-none opacity-50',
              'flex gap-2 opacity-30 hover:opacity-100 transition [&_svg>path]:hover:fill-delete',
            )}
          >
            <DeleteItemIcon />
          </button>
        )}
      </DialogTrigger>
      <DialogContent closeButtonHidden>
        <DialogHeader>
          <DialogTitle>Delete content</DialogTitle>
        </DialogHeader>
        <div className="flex flex-row gap-4 pt-4">
          <div className={'flex justify-center items-center'}>
            <DeleteItemAttentionIcon />
          </div>
          <div>
            <p className={'text-delete font-medium text-base'}>
              Are you sure you want to delete ‘{item.name}’ source irretrievably?
            </p>
          </div>
        </div>
        <div className="flex justify-center mt-5">
          <div className="flex gap-2.5">
            <Button variant="outline" type="button" className="min-w-24" onClick={close}>
              Cancel
            </Button>
            <Button className="min-w-24" onClick={onClick}>
              Delete
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
